import React, { useMemo } from 'react';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RestoreIcon from '@mui/icons-material/Restore';
import type { BackupBlob } from '@internal/backstage-plugin-bidone-backend';

export const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
};

interface Props {
  rows: BackupBlob[];
  selected: Set<string>;
  expanded: Set<string>;
  onToggle: (blobName: string) => void;
  onToggleAll: () => void;
  onToggleMany: (blobNames: string[], next: boolean) => void;
  onToggleExpanded: (release: string) => void;
  onRestore: (row: BackupBlob) => void;
}

interface Group {
  release: string;
  items: BackupBlob[];
  bytes: number;
  latest: string;
}

const COLUMN_COUNT = 4;

const BoldText = styled(Typography)({ fontWeight: 700 });

const formatWhen = (isoUtc: string): string => {
  if (!isoUtc) return '—';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return isoUtc;
  return d.toLocaleString();
};

// Bucket the flat list into per-release groups. Within each group, snapshots
// stay sorted newest-first (the input is already in that order); groups
// themselves are sorted by their newest snapshot, so the most recently
// touched release floats to the top.
const groupRows = (rows: BackupBlob[]): Group[] => {
  const byRelease = new Map<string, BackupBlob[]>();
  for (const r of rows) {
    const list = byRelease.get(r.release);
    if (list) list.push(r);
    else byRelease.set(r.release, [r]);
  }
  const groups: Group[] = [];
  for (const [release, items] of byRelease) {
    groups.push({
      release,
      items,
      bytes: items.reduce((acc, x) => acc + (x.sizeBytes || 0), 0),
      latest: items.reduce(
        (acc, x) => (x.takenAt > acc ? x.takenAt : acc),
        '',
      ),
    });
  }
  groups.sort((a, b) => b.latest.localeCompare(a.latest));
  return groups;
};

export const ReleaseChecklistBackupsTable: React.FC<Props> = ({
  rows,
  selected,
  expanded,
  onToggle,
  onToggleAll,
  onToggleMany,
  onToggleExpanded,
  onRestore,
}) => {
  const groups = useMemo(() => groupRows(rows), [rows]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && selected.size < rows.length;

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="release-checklist-backups">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={onToggleAll}
                inputProps={{ 'aria-label': 'select all backups' }}
                disabled={rows.length === 0}
              />
            </TableCell>
            <TableCell>Taken at (local)</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell align="right">Restore</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ padding: 16, textAlign: 'center' }}
                >
                  No backups yet. Run a backup or wait for the next scheduled
                  run.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {groups.map(group => {
            const groupNames = group.items.map(i => i.blobName);
            const selectedInGroup = groupNames.filter(n =>
              selected.has(n),
            ).length;
            const groupAllSelected =
              selectedInGroup === group.items.length && selectedInGroup > 0;
            const groupSomeSelected =
              selectedInGroup > 0 && selectedInGroup < group.items.length;
            const isExpanded = expanded.has(group.release);
            return (
              <React.Fragment key={group.release}>
                <TableRow
                  hover
                  onClick={() => onToggleExpanded(group.release)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      indeterminate={groupSomeSelected}
                      checked={groupAllSelected}
                      onChange={() =>
                        onToggleMany(groupNames, !groupAllSelected)
                      }
                      inputProps={{
                        'aria-label': `select all backups for ${group.release}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowRightIcon fontSize="small" />
                      )}
                      <BoldText variant="subtitle2">
                        {group.release}
                      </BoldText>
                      <Typography variant="caption" color="textSecondary">
                        ({group.items.length} backup
                        {group.items.length === 1 ? '' : 's'})
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <BoldText variant="subtitle2">
                      {formatBytes(group.bytes)}
                    </BoldText>
                  </TableCell>
                  <TableCell />
                </TableRow>
                {isExpanded &&
                  group.items.map(row => {
                    const isSelected = selected.has(row.blobName);
                    return (
                      <TableRow
                        key={row.blobName}
                        hover
                        selected={isSelected}
                        onClick={() => onToggle(row.blobName)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={e => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => onToggle(row.blobName)}
                            inputProps={{
                              'aria-label': `select ${row.blobName}`,
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ paddingLeft: 40 }}>
                          {formatWhen(row.takenAt)}
                        </TableCell>
                        <TableCell align="right">
                          {formatBytes(row.sizeBytes)}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={e => e.stopPropagation()}
                        >
                          <Tooltip title="Restore this backup">
                            <IconButton
                              size="small"
                              aria-label={`restore ${row.blobName}`}
                              onClick={() => onRestore(row)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
